import classNames from 'classnames';
import React, { useState } from 'react';
import {
  Block,
  BlockContent,
  BlockRenderElement,
  BlockVerticalAlignment,
  BlockViewState,
  ContextMenuState,
  LayoutMode,
  LayoutStage,
  RenderMode,
  Size,
} from './types';
import { SpringConfig, SpringValues } from '@react-spring/web';
import { DraggableData, DraggableEvent } from 'react-draggable';
import { Coords, useEditorStore } from './editor-store';
import { Nullable } from 'src/types/nullable.type';
import { MemoizedBlockView } from './block-view';
import { createPropsEqualsChecker } from './utils/create-props-equals-checker';
import { useRefCallback } from 'src/utils/react/ref-callback.hook';
import { shallow } from 'zustand/shallow';
import { getRelativeCanvasPosition } from './utils/get-relative-canvas-position';
import { useBlockViewStore } from './block-view-store';

export type BlockViewContainerProps = {
  renderElement: BlockRenderElement;
  style:
    | SpringValues<{ x: number; y: number; width: number; height: number; scale: number; opacity: number }>
    | { x: number; y: number; width: number; height: number; scale: number; opacity: number };
  onStart: (id: Block['id'], e: DraggableEvent, args: DraggableData) => void;
  onDrag: (id: Block['id'], e: DraggableEvent, args: DraggableData) => void;
  onStop: (id: Block['id'], e: DraggableEvent, args: DraggableData) => void;
  grabbing: boolean;
  elevated: boolean;
  layoutMode: LayoutMode;
  renderMode: RenderMode;
  layoutComplete: boolean;
  journeyUUID: Nullable<string>;
};

export const BlockViewContainer = ({
  renderElement,
  onStart,
  onDrag,
  onStop,
  grabbing,
  elevated,
  style,
  layoutMode,
  renderMode,
  layoutComplete,
  journeyUUID,
}: BlockViewContainerProps) => {
  const { block, rect } = renderElement;
  const blockId = block.id;

  // const previousStyle = React.useRef(style);
  // React.useEffect(() => {
  //   if (!fastDeepEqual(style, previousStyle.current)) {
  //     console.log('BlockViewContainer style changed', style, previousStyle.current);
  //     previousStyle.current = style;
  //   }
  // }, [style]);

  const setHoverBlockId = useEditorStore((state) => state.setHoverBlockId);
  const setFocusedBlockId = useEditorStore((state) => state.setFocusedBlockId);
  const dispatchUserEditorAction = useEditorStore((state) => state.dispatchUserEditorAction);
  const setBlockContentSize = useEditorStore((state) => state.setBlockContentSize);
  const setContextMenuState = useEditorStore((state) => state.setContextMenuState);
  const triggerBlockResourceLoad = useEditorStore((state) => state.triggerBlockResourceLoad);

  const setBlockViewState = useBlockViewStore((state) => state.setBlockViewState);

  // We don't use these attributes directly from the store, because that causes every instance of this view to re-render
  // when the selection changes, which is a performance bottleneck right now.
  // So, we subscribe to changes, check if they are applicable to this block and re-render only if they are.
  const [selected, setSelected] = useState(false);
  const [selectionCoords, setSelectionCoords] = useState<Nullable<Coords>>(null);
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const onSelectionChange = useRefCallback(
    (selectedBlockId: Nullable<Block['id']>, selectionCoords: Nullable<Coords>) => {
      const selected = selectedBlockId === block.id;
      setSelected(selected);
      setSelectionCoords(selected ? selectionCoords : null);
    },
    []
  );

  const onContextMenuChange = useRefCallback(
    (contextMenuState: Nullable<ContextMenuState>) => {
      const contextMenuOpen = contextMenuState?.entity.type === 'block' && contextMenuState?.entity.blockId === blockId;
      setContextMenuOpen(contextMenuOpen);
    },
    [blockId]
  );

  const onHoverChange = useRefCallback(
    (hoverBlockId: Nullable<Block['id']>) => {
      const isHovered = hoverBlockId === blockId;
      setIsHovered(isHovered);
    },
    [blockId]
  );

  const onFocusedChange = useRefCallback(
    (focusedBlockId: Nullable<Block['id']>) => {
      const hasFocus = focusedBlockId === blockId;
      setHasFocus(hasFocus);
    },
    [blockId]
  );

  React.useEffect(() => {
    const unsubscribe1 = useEditorStore.subscribe(
      (state) => [state.selectedBlockId, state.selectionCoords] as const,
      (args) => onSelectionChange(...args),
      { equalityFn: shallow }
    );
    const unsubscribe2 = useEditorStore.subscribe((state) => state.contextMenuState, onContextMenuChange);
    const unsubscribe3 = useEditorStore.subscribe((state) => state.hoverBlockId, onHoverChange);
    const unsubscribe4 = useEditorStore.subscribe((state) => state.focusedBlockId, onFocusedChange);

    return () => {
      unsubscribe1();
      unsubscribe2();
      unsubscribe3();
      unsubscribe4();
    };
  }, [blockId]);

  const setVerticalAlignCallback = useRefCallback((blockId: Block['id'], alignment: BlockVerticalAlignment) => {
    dispatchUserEditorAction({
      type: 'set-block-vertical-alignment',
      id: blockId,
      verticalAlignment: alignment,
    });
  }, []);

  const onDelete = useRefCallback((id: Block['id']) => {
    dispatchUserEditorAction({ type: 'delete-block', id });
  }, []);

  const onUploadCancel = useRefCallback((id: Block['id'], placeholderType: 'add-image' | 'add-file' = 'add-file') => {
    dispatchUserEditorAction({
      type: 'replace-block',
      id,
      content: { type: 'placeholder', placeholderType },
      layoutExtraParams: {},
    });
  }, []);

  const onUpdateBlockContent = useRefCallback((blockId: Block['id'], content: Partial<BlockContent>) => {
    dispatchUserEditorAction({
      type: 'update-block-content',
      id: blockId,
      content,
    });
  }, []);

  const onViewState = useRefCallback((blockId: Block['id'], viewState: BlockViewState) => {
    setBlockViewState(blockId, viewState);
  }, []);

  const setBlockContextMenuState = useRefCallback((id: Block['id'], pageX: number, pageY: number) => {
    setContextMenuState({
      entity: {
        type: 'block',
        blockId: id,
      },
      ...getRelativeCanvasPosition(pageX, pageY, 313),
    });
  }, []);

  const setFocusBlockIdCallback = useRefCallback((id: Block['id']) => {
    setFocusedBlockId(id);
  }, []);

  const setBlurBlockIdCallback = useRefCallback(
    (id: Block['id']) => {
      hasFocus && setFocusedBlockId(null);
    },
    [hasFocus]
  );

  const onHoverBlockCallback = useRefCallback(
    (id: Block['id'], hovering: boolean) => {
      if (hovering) {
        setHoverBlockId(id);
      } else {
        isHovered && setHoverBlockId(null);
      }
    },
    [isHovered]
  );

  const onFetchBlockResources = useRefCallback(triggerBlockResourceLoad, []);

  return (
    <MemoizedBlockView
      journeyUUID={journeyUUID}
      measuredSize={renderElement.measuredSize}
      block={renderElement.block}
      rect={rect}
      style={style}
      hasFocus={hasFocus}
      selectionCoords={selectionCoords}
      contextMenuOpen={contextMenuOpen}
      layoutMode={layoutMode}
      renderMode={renderMode}
      layoutComplete={layoutComplete}
      isFullRow={renderElement.isFullRow}
      onStart={onStart}
      onDrag={onDrag}
      onStop={onStop}
      onHover={onHoverBlockCallback}
      onDelete={onDelete}
      elevated={elevated}
      onBlockSize={setBlockContentSize}
      onUpdateBlockContent={onUpdateBlockContent}
      onViewState={onViewState}
      onSetVerticalAlignment={setVerticalAlignCallback}
      setContextMenuState={setBlockContextMenuState}
      onFetchBlockResources={onFetchBlockResources}
      onUploadCancel={onUploadCancel}
      grabbing={grabbing}
      onFocus={setFocusBlockIdCallback}
      onBlur={setBlurBlockIdCallback}
      selected={selected}
      hidden={renderElement.section.hidden}
      sectionID={renderElement.section.id}
    />
  );
};

export const MemoizedBlockViewContainer = React.memo(
  BlockViewContainer,
  createPropsEqualsChecker([], 'BlockViewContainer')
);